import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Aos from 'aos';
import 'aos/dist/aos.css';
import './App.css';
import './apis/axiosConfig';
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";

// Global Components
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Stickyicon from './components/Stickyicon';

// Lazy Load Pages
const Home = React.lazy(() => import('./pages/home/Home'));
const Treatment = React.lazy(() => import('./pages/services/Treatment'));
const Medicines = React.lazy(() => import('./pages/medicines/Medicines'));
const Medicinesdetails = React.lazy(() => import('./pages/medicines/Medicinesdetails'));
const About = React.lazy(() => import('./pages/about/About'));
const Ourdoctors = React.lazy(() => import('./pages/teams/Ourdoctors'));
const Ourteam = React.lazy(() => import('./pages/teams/Ourteam'));
const Contact = React.lazy(() => import('./pages/contact/Contact'));
const Login = React.lazy(() => import('./pages/user/Login'));
const Register = React.lazy(() => import('./pages/user/Register'));
const Cart = React.lazy(() => import('./pages/cart/Cart'));
const PhotoGallery = React.lazy(() => import('./pages/media/PhotoGallery'));
const MediaNews = React.lazy(() => import('./pages/media/MediaNews'));
const Doctordetails = React.lazy(() => import('./pages/drpathakji/Doctordetails'));
const Payment = React.lazy(() => import('./pages/chekout/Payment'));
const RenderRazorpay = React.lazy(() => import('./components/payment/RenderRazorpay'));
const Make_Appointment = React.lazy(() => import('./pages/appiontment/Make_Appointment'));
const Otp = React.lazy(() => import('./pages/user/Otp'));
const OTP_OPD = React.lazy(() => import('./pages/user/OTP_opd'));
const Confirm = React.lazy(() => import('./pages/payment/Confirm'));
const Myprofile = React.lazy(() => import('./pages/user/Myprofile'));
const Myorder = React.lazy(() => import('./pages/user/Myorder'));
const MyorderDetails = React.lazy(() => import('./pages/user/MyorderDetails'));
const Checkout = React.lazy(() => import('./pages/chekout/Checkout'));
const Testimonials = React.lazy(() => import('./pages/testimonials/Testimonials'));
const VideoTestimonials = React.lazy(() => import('./pages/testimonials/VideoTestimonials'));
const PrivateRouter = React.lazy(() => import('./routers/PrivateRouter'));
const F404 = React.lazy(() => import('./pages/404/F404'));

const AppRouteSkeleton = () => (
  <div className="app-route-skeleton" aria-hidden="true">
    <div className="container app-route-skeleton__container">
      <div className="app-route-skeleton__pill" />
      <div className="app-route-skeleton__title" />
      <div className="app-route-skeleton__line app-route-skeleton__line--wide" />
      <div className="app-route-skeleton__line app-route-skeleton__line--short" />

      <div className="app-route-skeleton__hero">
        <div className="app-route-skeleton__hero-copy">
          <div className="app-route-skeleton__block app-route-skeleton__block--lg" />
          <div className="app-route-skeleton__block app-route-skeleton__block--md" />
          <div className="app-route-skeleton__actions">
            <div className="app-route-skeleton__button" />
            <div className="app-route-skeleton__button app-route-skeleton__button--ghost" />
          </div>
        </div>
        <div className="app-route-skeleton__panel" />
      </div>

      <div className="app-route-skeleton__grid">
        <div className="app-route-skeleton__card">
          <div className="app-route-skeleton__media" />
          <div className="app-route-skeleton__card-line app-route-skeleton__card-line--title" />
          <div className="app-route-skeleton__card-line" />
          <div className="app-route-skeleton__card-line app-route-skeleton__card-line--short" />
        </div>
        <div className="app-route-skeleton__card">
          <div className="app-route-skeleton__media" />
          <div className="app-route-skeleton__card-line app-route-skeleton__card-line--title" />
          <div className="app-route-skeleton__card-line" />
          <div className="app-route-skeleton__card-line app-route-skeleton__card-line--short" />
        </div>
        <div className="app-route-skeleton__card">
          <div className="app-route-skeleton__media" />
          <div className="app-route-skeleton__card-line app-route-skeleton__card-line--title" />
          <div className="app-route-skeleton__card-line" />
          <div className="app-route-skeleton__card-line app-route-skeleton__card-line--short" />
        </div>
      </div>
    </div>
  </div>
);

// Initialize AOS animations
const App = () => {
  useEffect(() => {
    Aos.init();
  }, []);

  return (
      <Router>
      <ScrollToTop />
      <Header />
      <Suspense fallback={<AppRouteSkeleton />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Treatment />} />
          <Route path="/treatment" element={<Treatment />} />
          <Route path="/medicines" element={<Medicines />} />
          <Route path="/medicinesdetails/:id" element={<Medicinesdetails />} />
          <Route path="/aboutus" element={<About />} />
          <Route path="/ourdoctors" element={<Ourdoctors />} />
          <Route path="/ourteam" element={<Ourteam />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/photogallery" element={<PhotoGallery />} />
          <Route path="/medianews" element={<MediaNews />} />
          <Route path="/doctordetails" element={<Doctordetails />} />
          <Route path="/payment" element={<Payment />} />
          <Route path="/online-payment" element={<RenderRazorpay />} />
          <Route path="/makeappointment" element={<Make_Appointment />} />
          <Route path="/otp" element={<Otp />} />
          <Route path="/otpopd" element={<OTP_OPD/>} />
          <Route path="/receipt/:id" element={<Confirm />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/video-testimonials" element={<VideoTestimonials />} />

          {/* Private Routes */}
          <Route element={<PrivateRouter />}>
            <Route path="/myorder" element={<Myorder />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/myorder/:id" element={<MyorderDetails />} />
            <Route path="/myprofile" element={<Myprofile />} />
          </Route>

          {/* Catch-all Route */}
          <Route path="*" element={<F404 />} />
        </Routes>
      </Suspense>
      <Stickyicon />
      <Footer />
    </Router>
  );
};

export default App;
